import { Grid, Tooltip, Typography } from '@material-ui/core'
import withStyles from '@material-ui/core/styles/withStyles'
import { getStyles } from 'isotope-client'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { compose } from 'redux'
import { DAY_PERIOD, WEATHER_TILE_TYPE } from '../tool/contextePedoclimatique.constants'
import { getHumidityIcon, getSoilIcon, getTemperatureIcon, getWeatherIcon, getWindIcon } from '../tool/contextePedoclimatique.utils'
import { colors } from '../../../../../../utils/constants'
import EditIcon from '@material-ui/icons/Edit'
import IconButton from '../../../../../../components/IconButton'
import { injectWeatherUpdateForm } from '../service/weatherUpdateFormInjector'
import { ZONE_TYPE } from '../../../tool/tourPlaine.constants'
import ManualIcon from '@material-ui/icons/HowToReg'
import FormattedMessageWithBold from '../../../../../../components/FormattedMessageWithBold'
import { useIntl } from 'react-intl'

/**
 * Styles
 */
const styles = () => getStyles({
	content: {
		height: 125,
		borderRadius: 10,
		border: `1px solid ${colors.lightPrimary}`,
		rowGap: 5
	},
	title: {
		fontWeight: 400,
		lineHeight: '14px',
		fontStyle: 'italic',
		fontSize: 10,
		color: colors.primary,
		textAlign: 'center',
		verticalAlign: 'bottom',
		padding: '0 7px'
	},
	iconBtn: {
		height: 25,
		width: 25,
		border: `1px solid ${colors.primary}`
	},
	iconBtnDisabled: {
		height: 25,
		width: 25,
		border: `1px solid ${colors.icon}`,
		backgroundColor: `${colors.foBackgroundColorPage} !important`
	},
	iconBtnContainer: {
		height: 35,
		justifyContent: 'center',
		alignItems: 'center',
		display: 'flex'
	},
	icon: {
		height: 14,
		width: 14
	}
})

const CustomTooltip = withStyles(() => ({
	tooltip: {
		backgroundColor: colors.etapeBackground,
		color: 'black',
		width: 250,
		borderRadius: 10,
		boxShadow: '0px 2px 4px gray'
	},
	arrow: {
		color: colors.etapeBackground
	}
}))(Tooltip)

/**
 * WeatherTile
 * @param classes
 * @param dayKey id
 * @param type type de tuile, meteo, humidité, vent, température ou nature du sol
 * @param zone serre ou exterieur
 * @param isDaySplitted if true show weather icon for AM and PM
 * @param data -> data ou on va chercher la donnée
 * @param weekMode -> should be small or not
 * @param openWeatherUpdateForm -> open form
 * @param isDashboard -> from dashboard
 * @param disabled -> edition disabled
 * @returns {JSX.Element}
 * @constructor
 */
const WeatherTile = ({
	classes,
	dayKey,
	type,
	zone,
	isDaySplitted,
	data,
	weekMode,
	openWeatherUpdateForm,
	isDashboard,
	disabled
}) => {
	const { formatMessage, formatDate } = useIntl()

	const getPedoclimatiqueValue = (dayPeriod) => {
		if (data[dayPeriod][zone] && data[dayPeriod][zone][type]) {
			return data[dayPeriod][zone][type]
		}
		return undefined
	}

	/**
	 * Manage the row width
	 * @returns {number}
	 */
	const computeRowWidth = () => {
		if (weekMode) {
			return zone === ZONE_TYPE.INSIDE ? 94.25 : 74.4
		}
		return zone === ZONE_TYPE.INSIDE ? (isDashboard ? 88.25 : 102.25) : (isDashboard ? 70 : 80.8)
	}

	const renderHalfDayValue = (dayPeriod) => {
		// Récupération de la valeur à afficher
		const pedoclimatiqueValue = getPedoclimatiqueValue(dayPeriod)
		// On priorise manual
		const value = pedoclimatiqueValue?.manual ?? pedoclimatiqueValue?.external
		const isManualValue = !!pedoclimatiqueValue?.manual

		// Icône
		let icon = undefined
		if (value) {
			switch (type) {
				case WEATHER_TILE_TYPE.WEATHER:
					icon = getWeatherIcon(value)
					break
				case WEATHER_TILE_TYPE.HUMIDITY:
					icon = getHumidityIcon(value)
					break
				case WEATHER_TILE_TYPE.WIND:
					icon = getWindIcon(value)
					break
				case WEATHER_TILE_TYPE.TEMPERATURE:
					icon = getTemperatureIcon(value)
					break
				case WEATHER_TILE_TYPE.SOIL:
					icon = getSoilIcon(value)
					break
				default:
					icon = undefined
			}
		}

		// Tooltip info
		let lastUpdated = undefined
		let author = undefined
		if (data[dayPeriod][zone]) {
			lastUpdated = isManualValue ? data[dayPeriod][zone].manualLastUpdated : data[dayPeriod][zone].externalLastUpdated
			author = data[dayPeriod][zone].author
		}
		const date = formatDate(new Date(lastUpdated), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

		return <Grid item container direction="row" justify="center" alignItems="center" style={{ columnGap: zone === ZONE_TYPE.INSIDE ? 20 : 15 }}>
			<CustomTooltip
				title={<Grid container direction="column">
					{lastUpdated && <Grid item><FormattedMessageWithBold id="tourPlaine.contextePedoclimatique.tileTooltip.updated" values={{ date }} /></Grid>}
					{pedoclimatiqueValue?.manual && <>
						{author && <Grid item><FormattedMessageWithBold id="tourPlaine.contextePedoclimatique.tileTooltip.author" values={{ author }} /></Grid>}
						{pedoclimatiqueValue?.external && <Grid item>
							<FormattedMessageWithBold
								id="tourPlaine.contextePedoclimatique.tileTooltip.api"
								values={{ data: formatMessage({ id: `tourPlaine.contextePedoclimatique.icons.${type}.${pedoclimatiqueValue?.external}.description` })  }}
							/>
						</Grid>}
					</>}
				</Grid>}
				placement="top-start"
			>
				<div style={{ position: 'relative', cursor: 'pointer' }}>
					{icon}
					{!!pedoclimatiqueValue?.manual &&
						<ManualIcon
							style={{
								position: 'absolute',
								top: 0,
								right: -12,
								width: '14px',
								height: '14px',
								color: colors.primary
							}}
						/>}
				</div>
			</CustomTooltip>
			{
				!isDashboard && <div className={classes.iconBtnContainer}>
					<IconButton
						className={disabled ? classes.iconBtnDisabled : classes.iconBtn}
						onClick={() => {
							const pedoclimatiqueValue = getPedoclimatiqueValue(dayPeriod)
							const value = pedoclimatiqueValue?.manual ?? pedoclimatiqueValue?.external
							openWeatherUpdateForm(dayKey, dayPeriod, zone, type, value, false)
						}}
						disabled={disabled}
					>
						<EditIcon
							style={{
								width: '14px',
								height: '14px',
								color: disabled ? colors.icon : colors.primary
							}}
						/>
					</IconButton>
				</div>}
		</Grid>
	}

	return (
		<Grid container justify="center" className={classes.content} style={{ width: computeRowWidth() }}>
			<Grid item container justify="center" alignItems="flex-start" style={{ height: isDaySplitted ? '28%' : '40%', paddingTop: 15 }}>
				<Typography className={classes.title}>
					<FormattedMessage id={`tourPlaine.contextePedoclimatique.tileType.${type}`} />
				</Typography>
			</Grid>
			<Grid item container justify="center" alignItems="flex-start" style={{ height: isDaySplitted ? undefined : '45%' }}>
				{renderHalfDayValue(DAY_PERIOD.AM)}
			</Grid>
			{isDaySplitted && (
				<Grid item container justify="center" alignItems="flex-start">
					{renderHalfDayValue(DAY_PERIOD.PM)}
				</Grid>
			)}
		</Grid>
	)
}

export default compose(
	injectWeatherUpdateForm,
	withStyles(styles)
)(WeatherTile)